import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { BrowserRouter, HashRouter, Switch, Route } from 'react-router-dom'

import Loading from './components/common/Loading'
import SensorDataProvider from './network/SensorDataProvider'
import LedgerGuide from './components/guides/LedgerGuidePage'
import BlogArticlePage from './components/blog/BlogArticlePage'

import {
  Home,
  Balances,
  NoFound,
  TransactionHistory,
  RecoverKeystore,
  NewAccount,
  UnlockAccount,
  Exchange,
  SendMobile,
  TradePairMobile,
  OpenOrders,
  OrderHistory,
  TradeHistory,
  FeesHistory,
  History,
  UnlockFromCoinomiAndRedirect,
  UnlockFromInfinitoAndRedirect,
  Terms,
  Token,
  EditWallet,
  Privacy,
} from './asyncRoute'

import BncClientProvider from './containers/context/BNCClientProvider'
import WalletConnectProvider from './containers/context/WalletConnectProvider'
import CommonDataProvider from './network/CommonDataProvider'
import { isMobile } from './utils/bomUtils'
import { RouteWithRedirect, addLocalePrefix } from './i18n/util'
import bnbDeviceInfo from './utils/fingerPrint'
import collect from './utils/performance'
import isElectron from './utils/isElectron'
import { setFingerPrint } from './redux/global/action'

/* eslint-disable-next-line */
const Router = (() => {
  if (!!window.chrome && !!window.chrome.extension) return HashRouter
  if (isElectron()) return HashRouter
  return BrowserRouter
})()

class RootRoute extends PureComponent {
  static propTypes = {
    isLoading: PropTypes.bool,
    isHardware: PropTypes.bool,
    showTradePairsMobile: PropTypes.bool,
    deviceInfo: PropTypes.object,
    address: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
  }

  static defaultProps = {
    isLoading: false,
    isHardware: false,
    showTradePairsMobile: false,
    deviceInfo: {},
    address: '',
  }

  componentDidMount() {
    window.addEventListener('resize', this._resizeWindow)
    this._getDeviceInfo()
    // will remove in next version
    window.localStorage.removeItem('network')
    collect()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._resizeWindow)
  }

  _getDeviceInfo = () => {
    const { dispatch, address } = this.props
    const handle = setInterval(() => {
      const deviceInfo = bnbDeviceInfo.getInfo(address)
      if (deviceInfo.webgl_vendor && deviceInfo.webgl_renderer) {
        dispatch(setFingerPrint(deviceInfo))
        clearInterval(handle)
      }
    }, 2000)
  }

  render() {
    const { isLoading, isHardware, showTradePairsMobile } = this.props
    return (
      <BncClientProvider>
        <WalletConnectProvider>
          <Router>
            <>
              <Switch>
                <RouteWithRedirect exact={true} path="/" component={Home} />
                <RouteWithRedirect exact={true} path={addLocalePrefix('/')} component={Home} />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/trade')}
                  component={Exchange}
                />
                <RouteWithRedirect exact={true} path="/trade/:symbol" component={Exchange} />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/trade/:symbol')}
                  component={Exchange}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/index.html')}
                  component={Exchange}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/create')}
                  component={NewAccount}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/unlock')}
                  component={UnlockAccount}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/openOrders')}
                  component={OpenOrders}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/orderHistory')}
                  component={OrderHistory}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/tradeHistory')}
                  component={TradeHistory}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/feeHistory')}
                  component={FeesHistory}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/balances')}
                  component={Balances}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/balances/:symbol')}
                  component={SendMobile}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/transactionHistory')}
                  component={TransactionHistory}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/history')}
                  component={History}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/terms')}
                  component={Terms}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/edit-wallet')}
                  component={EditWallet}
                />

                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/tokens')}
                  component={Token}
                />

                {/* Blog */}
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/blog/:slug')}
                  component={BlogArticlePage}
                />

                {/* Ledger guide redirect used by Ledger Live */}
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/guides/ledger')}
                  component={LedgerGuide}
                />

                {/* Software wallet entry points, used by software wallet apps */}
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/coinomi')}
                  component={UnlockFromCoinomiAndRedirect}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/infinito')}
                  component={UnlockFromInfinitoAndRedirect}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/recover')}
                  component={RecoverKeystore}
                />
                <RouteWithRedirect
                  exact={true}
                  path={addLocalePrefix('/privacy')}
                  component={Privacy}
                />
                {/* 404 */}
                <Route component={NoFound} />
              </Switch>

              {/* Loading spinner */}
              {/* NOTE: Temporarily disabled for hardware wallets */}
              {isLoading && !isHardware && <Loading />}
              {isElectron() && <SensorDataProvider />}
              <CommonDataProvider />
              {isMobile() && showTradePairsMobile && (
                <TradePairMobile hide={!showTradePairsMobile} />
              )}
            </>
          </Router>
        </WalletConnectProvider>
      </BncClientProvider>
    )
  }
}

const mapStateToProps = ({
  globalData: { isLoading, deviceInfo },
  account: {
    address,
    flags: { isHardware },
  },
  exchange: { showTradePairsMobile },
}) => ({
  isLoading,
  isHardware,
  showTradePairsMobile,
  deviceInfo,
  address,
})

export default connect(mapStateToProps)(RootRoute)
